import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, AlertTriangle, Heart, UserCheck, FileText, Briefcase, TrendingUp, Save, RotateCcw, CreditCard } from 'lucide-react';
import { callAdminGetVetaxisOperations, callAdminUpdateVetaxisClinicOverrides, callAdminApplyVetaxisClinicBilling } from '../../services/admin';

type Clinic = {
  id: string;
  name: string;
  ownerId: string | null;
  subscriptionPlanId: string;
  baseSeatLimit?: number;
};

type VetUser = {
  uid: string;
  email: string | null;
  name: string | null;
  primaryClinicId: string | null;
  clinicMemberships: Array<{
    clinicId: string;
    role: string;
    status: string;
  }>;
  subscription: {
    planId: string;
    status: string;
  };
};

type OpsData = {
  clinics: Clinic[];
  users: VetUser[];
  clinicUsage: Record<string, ClinicUsage>;
};

type LimitValue = number | string;

type ClinicUsage = {
  plan: {
    id: string;
    name: string;
    price: number;
    limits: Record<string, LimitValue>;
    overrides?: {
      price?: number;
      limits?: Record<string, LimitValue>;
    };
  };
  counts: {
    users: number;
    animals: number;
    clients: number;
  };
  monthly: {
    invoices: { currentMonth: number; previousMonth: number };
    clinicalJobs: { currentMonth: number; previousMonth: number };
  };
};

const statusPill = (value: string) => {
  const v = (value || '').toLowerCase();
  if (v === 'active' || v === 'trialing') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (v === 'past_due' || v === 'canceled') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
};

const formatLimit = (value: LimitValue | undefined) => {
  if (value === undefined || value === null || value === '') return '—';
  if (String(value).toLowerCase() === 'unlimited') return 'Unlimited';
  return String(value);
};

const percentUsed = (count: number, limit: LimitValue | undefined) => {
  if (typeof limit !== 'number' || limit <= 0) return null;
  return Math.min(100, Math.round((count / limit) * 100));
};

const trendLabel = (current: number, previous: number) => {
  if (previous === 0 && current === 0) return 'No activity';
  if (previous === 0) return '+100%';
  const change = Math.round(((current - previous) / previous) * 100);
  return `${change > 0 ? '+' : ''}${change}%`;
};

const overrideFields = [
  { key: 'usersIncluded', label: 'Users' },
  { key: 'animals', label: 'Animals' },
  { key: 'clients', label: 'Clients' },
  { key: 'invoicesPerMonth', label: 'Invoices/month' },
  { key: 'clinicalJobsPerMonth', label: 'Clinical jobs/month' },
] as const;

type OverrideLimitKey = typeof overrideFields[number]['key'];

type OverrideForm = {
  price: string;
  limits: Record<OverrideLimitKey, string>;
};

const emptyOverrideForm = (): OverrideForm => ({
  price: '',
  limits: {
    usersIncluded: '',
    animals: '',
    clients: '',
    invoicesPerMonth: '',
    clinicalJobsPerMonth: '',
  },
});

const formatOverrideInput = (value: LimitValue | undefined) =>
  value === undefined || value === null ? '' : String(value);

const normalizeOverrideInput = (value: string) => {
  const clean = value.trim();
  if (!clean) return undefined;
  if (clean.toLowerCase() === 'unlimited') return 'unlimited';
  const numeric = Number(clean);
  if (!Number.isFinite(numeric) || numeric < 0) return undefined;
  return Math.floor(numeric);
};

const AdminVetAxisClinicUsers: React.FC = () => {
  const params = useParams<{ clinicId: string }>();
  const clinicId = decodeURIComponent(params.clinicId || '').trim();
  const [ops, setOps] = useState<OpsData>({ clinics: [], users: [], clinicUsage: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideForm, setOverrideForm] = useState<OverrideForm>(() => emptyOverrideForm());
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [applyingBilling, setApplyingBilling] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await callAdminGetVetaxisOperations({
          limitClinics: 500,
          limitUsers: 2000,
          clinicId,
          includeUsage: true,
        });
        if (!mounted) return;
        setOps({ clinics: data.clinics || [], users: data.users || [], clinicUsage: data.clinicUsage || {} });
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load clinic users');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [clinicId, reloadKey]);

  const clinic = useMemo(
    () => ops.clinics.find((item) => item.id === clinicId) || null,
    [ops.clinics, clinicId]
  );

  const linkedUsers = useMemo(
    () => ops.users.filter((user) => {
      if (user.primaryClinicId === clinicId) return true;
      return Array.isArray(user.clinicMemberships)
        && user.clinicMemberships.some((membership) => membership?.clinicId === clinicId);
    }),
    [ops.users, clinicId]
  );

  const membershipForClinic = (user: VetUser) =>
    (user.clinicMemberships || []).find((membership) => membership.clinicId === clinicId) || null;

  const usage = clinicId ? ops.clinicUsage[clinicId] : undefined;

  const limitCards = usage ? [
    { label: 'Users', count: usage.counts.users, limit: usage.plan.limits.usersIncluded ?? clinic?.baseSeatLimit, icon: Users },
    { label: 'Animals', count: usage.counts.animals, limit: usage.plan.limits.animals, icon: Heart },
    { label: 'Clients', count: usage.counts.clients, limit: usage.plan.limits.clients, icon: UserCheck },
    { label: 'Invoices this month', count: usage.monthly.invoices.currentMonth, limit: usage.plan.limits.invoicesPerMonth, icon: FileText },
    { label: 'Clinical jobs this month', count: usage.monthly.clinicalJobs.currentMonth, limit: usage.plan.limits.clinicalJobsPerMonth, icon: Briefcase },
  ] : [];

  const trendCards = usage ? [
    { label: 'Invoices', current: usage.monthly.invoices.currentMonth, previous: usage.monthly.invoices.previousMonth },
    { label: 'Clinical jobs', current: usage.monthly.clinicalJobs.currentMonth, previous: usage.monthly.clinicalJobs.previousMonth },
  ] : [];

  useEffect(() => {
    if (!usage) return;
    const overrides = usage.plan.overrides || {};
    setOverrideForm({
      price: formatOverrideInput(overrides.price),
      limits: {
        usersIncluded: formatOverrideInput(overrides.limits?.usersIncluded),
        animals: formatOverrideInput(overrides.limits?.animals),
        clients: formatOverrideInput(overrides.limits?.clients),
        invoicesPerMonth: formatOverrideInput(overrides.limits?.invoicesPerMonth),
        clinicalJobsPerMonth: formatOverrideInput(overrides.limits?.clinicalJobsPerMonth),
      },
    });
  }, [usage]);

  const saveOverrides = async () => {
    if (!clinicId) return;
    setSavingOverrides(true);
    setError(null);
    setOverrideMessage(null);
    try {
      const limits: Partial<Record<OverrideLimitKey, number | string>> = {};
      overrideFields.forEach((field) => {
        const value = normalizeOverrideInput(overrideForm.limits[field.key]);
        if (value !== undefined) limits[field.key] = value;
      });
      const priceValue = normalizeOverrideInput(overrideForm.price);
      const overrides = {
        ...(typeof priceValue === 'number' ? { price: priceValue } : {}),
        ...(Object.keys(limits).length > 0 ? { limits } : {}),
      };
      await callAdminUpdateVetaxisClinicOverrides({ clinicId, overrides });
      setOverrideMessage('Clinic overrides saved.');
      setReloadKey((value) => value + 1);
    } catch (err: any) {
      setError(err?.message || 'Failed to save clinic overrides');
    } finally {
      setSavingOverrides(false);
    }
  };

  const clearOverrides = async () => {
    if (!clinicId) return;
    setSavingOverrides(true);
    setError(null);
    setOverrideMessage(null);
    try {
      await callAdminUpdateVetaxisClinicOverrides({ clinicId, clear: true });
      setOverrideMessage('Clinic overrides cleared.');
      setOverrideForm(emptyOverrideForm());
      setReloadKey((value) => value + 1);
    } catch (err: any) {
      setError(err?.message || 'Failed to clear clinic overrides');
    } finally {
      setSavingOverrides(false);
    }
  };

  const applyBilling = async () => {
    if (!clinicId || !usage) return;
    const confirmed = window.confirm(
      `Update the PayFast subscription for this clinic's owner to R${usage.plan.price}/month? ` +
      'This changes real billing from the next cycle onward.'
    );
    if (!confirmed) return;
    setApplyingBilling(true);
    setError(null);
    setOverrideMessage(null);
    try {
      const result = await callAdminApplyVetaxisClinicBilling({ clinicId });
      setOverrideMessage(`PayFast subscription updated to R${result.amount}/month (from next cycle).`);
    } catch (err: any) {
      setError(err?.message || 'Failed to apply billing to PayFast');
    } finally {
      setApplyingBilling(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin/vetaxis/clinics" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Clinics
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Clinic Users</h1>
              <p className="text-slate-600 dark:text-gray-400">
                {clinic ? `${clinic.name} (${clinic.id})` : clinicId || 'Unknown clinic'}
              </p>
            </div>
          </div>
        </motion.div>

        {error && <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>}

        {!loading && !clinic && (
          <div className="mb-6 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span>Clinic not found. It may have been removed.</span>
          </div>
        )}

        {!loading && clinic && usage && (
          <motion.div
            className="space-y-6 mb-8"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="glass-effect rounded-2xl p-5 shadow-glass">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-wide text-slate-500 dark:text-gray-400">Current plan</p>
                  <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">{usage.plan.name}</h2>
                  <p className="text-sm text-slate-600 dark:text-gray-300">
                    R{usage.plan.price}/month · Plan ID: <span className="font-mono">{usage.plan.id}</span>
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500 dark:text-gray-400">Animals</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{formatLimit(usage.plan.limits.animals)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-gray-400">Clients</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{formatLimit(usage.plan.limits.clients)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-gray-400">Invoices/month</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{formatLimit(usage.plan.limits.invoicesPerMonth)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-gray-400">Jobs/month</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{formatLimit(usage.plan.limits.clinicalJobsPerMonth)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-effect rounded-2xl p-5 shadow-glass">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Clinic overrides</h2>
                  <p className="text-sm text-slate-500 dark:text-gray-400">
                    Override this clinic only. Blank fields keep the plan default.
                  </p>
                </div>
                {overrideMessage && (
                  <p className="text-sm text-green-700 dark:text-green-400">{overrideMessage}</p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm text-slate-500 dark:text-gray-400">Price/month</span>
                  <input
                    type="text"
                    value={overrideForm.price}
                    onChange={(event) => setOverrideForm((current) => ({ ...current, price: event.target.value }))}
                    placeholder={String(usage.plan.price)}
                    className="mt-1 w-full rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>
                {overrideFields.map((field) => (
                  <label key={field.key} className="block">
                    <span className="text-sm text-slate-500 dark:text-gray-400">{field.label}</span>
                    <input
                      type="text"
                      value={overrideForm.limits[field.key]}
                      onChange={(event) => setOverrideForm((current) => ({
                        ...current,
                        limits: { ...current.limits, [field.key]: event.target.value },
                      }))}
                      placeholder={formatLimit(usage.plan.limits[field.key])}
                      className="mt-1 w-full rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={saveOverrides}
                  disabled={savingOverrides}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {savingOverrides ? 'Saving...' : 'Save overrides'}
                </button>
                <button
                  type="button"
                  onClick={clearOverrides}
                  disabled={savingOverrides}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-gray-700 text-sm font-medium text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-60"
                >
                  <RotateCcw className="w-4 h-4" />
                  Clear overrides
                </button>
                <button
                  type="button"
                  onClick={applyBilling}
                  disabled={applyingBilling || savingOverrides}
                  title="Update the owner's live PayFast subscription to the current effective price"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 dark:border-amber-500/40 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-60"
                >
                  <CreditCard className="w-4 h-4" />
                  {applyingBilling ? 'Applying...' : 'Apply to billing'}
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500 dark:text-gray-400">
                Saving overrides updates the limits and quoted price only. Use <span className="font-medium">Apply to billing</span> to push the current price to the owner&apos;s PayFast subscription (takes effect next cycle).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {limitCards.map((item) => {
                const Icon = item.icon;
                const pct = percentUsed(item.count, item.limit);
                return (
                  <div key={item.label} className="glass-effect rounded-2xl p-5 shadow-glass">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm text-slate-500 dark:text-gray-400">{item.label}</p>
                        <p className="text-2xl font-semibold text-slate-900 dark:text-white">
                          {item.count} <span className="text-sm font-normal text-slate-500 dark:text-gray-400">/ {formatLimit(item.limit)}</span>
                        </p>
                      </div>
                      <div className="p-2 rounded-lg bg-primary-500/10 dark:bg-primary-400/10">
                        <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                    </div>
                    {pct !== null && (
                      <div className="mt-4">
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-gray-800 overflow-hidden">
                          <div className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">{pct}% used</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trendCards.map((item) => (
                <div key={item.label} className="glass-effect rounded-2xl p-5 shadow-glass">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-gray-400">{item.label}</p>
                      <p className="text-2xl font-semibold text-slate-900 dark:text-white">{item.current}</p>
                    </div>
                    <div className="text-right">
                      <div className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400">
                        <TrendingUp className="w-4 h-4" />
                        {trendLabel(item.current, item.previous)}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-gray-400">Previous month: {item.previous}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="glass-effect rounded-2xl p-8 text-center text-slate-600 dark:text-gray-400">Loading clinic users...</div>
        ) : linkedUsers.length === 0 ? (
          <div className="glass-effect rounded-2xl p-8 text-center text-slate-600 dark:text-gray-400">No linked users found for this clinic.</div>
        ) : (
          <motion.div
            className="glass-effect rounded-2xl p-4 shadow-glass"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-gray-400 border-b border-slate-200 dark:border-gray-700">
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">UID</th>
                    <th className="py-2 pr-3">Role</th>
                    <th className="py-2 pr-3">Membership Status</th>
                    <th className="py-2 pr-3">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedUsers.map((user) => {
                    const membership = membershipForClinic(user);
                    const role = membership?.role || (user.primaryClinicId === clinicId ? 'owner' : 'member');
                    const membershipStatus = membership?.status || 'active';
                    return (
                      <tr key={user.uid} className="border-b border-slate-100 dark:border-gray-800">
                        <td className="py-2 pr-3">{user.email || user.name || 'Unknown user'}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{user.uid}</td>
                        <td className="py-2 pr-3 capitalize">{role}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(membershipStatus)}`}>{membershipStatus}</span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="capitalize">{user.subscription?.planId || '—'}</span>
                          <span className="mx-2 text-slate-300">•</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(user.subscription?.status || 'none')}`}>
                            {user.subscription?.status || 'none'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminVetAxisClinicUsers;
